/**
 * Mock data for development — replaced by real API calls when backend is ready.
 * All prices are in cents (USD).
 */
import type { Artwork, Artist, Exhibition, ArtFair, TeamMember, Category, DropshopCollection, NewsArticle } from '@/types';

export const MOCK_ARTISTS: Artist[] = [
  {
    _id: 'a1',
    name: { ka: 'ნინო მაჭავარიანი', en: 'Nino Machavariani' },
    bio: {
      ka: 'თანამედროვე ფოტოგრაფიის ოსტატი, რომელიც კავკასიის ბუნებრივ სილამაზეს აფიქსირებს.',
      en: 'Contemporary photography master capturing the natural beauty of the Caucasus.',
    },
    portrait: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=400&q=80',
    slug: 'nino-machavariani',
    artworkCount: 24,
    isActive: true,
  },
  {
    _id: 'a2',
    name: { ka: 'გიორგი ბერიძე', en: 'Giorgi Beridze' },
    bio: {
      ka: 'აბსტრაქტული ფოტოგრაფიის პიონერი საქართველოში.',
      en: 'Pioneer of abstract photography in Georgia.',
    },
    portrait: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80',
    slug: 'giorgi-beridze',
    artworkCount: 18,
    isActive: true,
  },
  {
    _id: 'a3',
    name: { ka: 'თამარ ჯავახიშვილი', en: 'Tamar Javakhishvili' },
    bio: {
      ka: 'პორტრეტისტი, რომელიც ადამიანის ემოციების სიღრმეს იკვლევს.',
      en: 'Portrait artist exploring the depth of human emotions.',
    },
    portrait: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=400&q=80',
    slug: 'tamar-javakhishvili',
    artworkCount: 15,
    isActive: true,
  },
];

export const MOCK_CATEGORIES: Category[] = [
  {
    _id: 'cat_landscape', name: { en: 'Landscape', ka: 'ლანდშაფტი' }, slug: 'landscape',
    description: { en: 'Nature and landscape photography', ka: 'ბუნებისა და ლანდშაფტის ფოტოგრაფია' },
    displayOrder: 1, isActive: true, subcategories: [
      { _id: 'sub_mountain', name: { en: 'Mountain', ka: 'მთა' }, slug: 'mountain', parentCategoryId: 'cat_landscape', displayOrder: 1, isActive: true },
      { _id: 'sub_coastal', name: { en: 'Coastal', ka: 'სანაპირო' }, slug: 'coastal', parentCategoryId: 'cat_landscape', displayOrder: 2, isActive: true },
    ],
  },
  {
    _id: 'cat_urban', name: { en: 'Urban', ka: 'ურბანული' }, slug: 'urban',
    description: { en: 'City and street photography', ka: 'ქალაქისა და ქუჩის ფოტოგრაფია' },
    displayOrder: 2, isActive: true, subcategories: [
      { _id: 'sub_street', name: { en: 'Street', ka: 'ქუჩა' }, slug: 'street', parentCategoryId: 'cat_urban', displayOrder: 1, isActive: true },
      { _id: 'sub_nightscape', name: { en: 'Nightscape', ka: 'ღამის ხედი' }, slug: 'nightscape', parentCategoryId: 'cat_urban', displayOrder: 2, isActive: true },
    ],
  },
  {
    _id: 'cat_abstract', name: { en: 'Abstract', ka: 'აბსტრაქტული' }, slug: 'abstract',
    description: { en: 'Abstract and experimental art', ka: 'აბსტრაქტული და ექსპერიმენტული ხელოვნება' },
    displayOrder: 3, isActive: true, subcategories: [],
  },
  {
    _id: 'cat_architecture', name: { en: 'Architecture', ka: 'არქიტექტურა' }, slug: 'architecture',
    description: { en: 'Architectural photography', ka: 'არქიტექტურული ფოტოგრაფია' },
    displayOrder: 4, isActive: true, subcategories: [],
  },
  {
    _id: 'cat_fineart', name: { en: 'Fine Art', ka: 'სახელოვნებო' }, slug: 'fine-art',
    description: { en: 'Fine art photography and prints', ka: 'სახელოვნებო ფოტოგრაფია და ბეჭდვები' },
    displayOrder: 5, isActive: true, subcategories: [
      { _id: 'sub_portrait', name: { en: 'Portrait', ka: 'პორტრეტი' }, slug: 'portrait', parentCategoryId: 'cat_fineart', displayOrder: 1, isActive: true },
    ],
  },
  {
    _id: 'cat_giftcard', name: { en: 'Gift Cards', ka: 'სასაჩუქრე ბარათები' }, slug: 'gift-cards',
    description: { en: 'Gift cards for art lovers', ka: 'სასაჩუქრე ბარათები ხელოვნების მოყვარულთათვის' },
    displayOrder: 6, isActive: true, subcategories: [],
  },
];

export const MOCK_ARTWORKS: Artwork[] = [
  {
    _id: 'w1',
    title: { ka: 'კავკასიის გარიჟრაჟი', en: 'Caucasus Dawn' },
    artist: MOCK_ARTISTS[0]!,
    description: {
      ka: 'მთების მაჯესტური ხედი გარიჟრაჟზე, სადაც სინათლე და ჩრდილი ტანგოს ცეკვავს.',
      en: 'A majestic mountain view at dawn, where light and shadow dance a tango.',
    },
    images: [
      'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1600&q=80',
      'https://images.unsplash.com/photo-1582555172866-f73bb12a2ab3?auto=format&fit=crop&w=800&q=80',
    ],
    category: 'Landscape', categoryId: 'cat_landscape', subcategoryId: 'sub_mountain',
    medium: { ka: 'არქივალური პიგმენტის ბეჭდვა', en: 'Archival Pigment Print' },
    year: 2024,
    dimensions: { width: 120, height: 80, unit: 'cm' },
    sizeClass: 'large',
    price: { amount: 24000, currency: 'USD' },
    gelPrice: { amount: 65000, currency: 'GEL' },
    rarity: 'limited',
    edition: { current: 3, total: 50 },
    isForSale: true,
    isFeatured: true,
    viewCount: 342,
    slug: 'caucasus-dawn',
    videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-abstract-flowing-purple-and-blue-neon-liquid-27201-large.mp4',
  },
  {
    _id: 'w2',
    title: { ka: 'თბილისის ღამე', en: 'Tbilisi Nightscape' },
    artist: MOCK_ARTISTS[1]!,
    description: {
      ka: 'ძველი თბილისის ქუჩები ღამის განათებაში.',
      en: 'Streets of old Tbilisi in nocturnal illumination.',
    },
    images: [
      'https://images.unsplash.com/photo-1633167606207-d840b5070fc2?auto=format&fit=crop&w=1600&q=80',
    ],
    category: 'Urban', categoryId: 'cat_urban', subcategoryId: 'sub_nightscape',
    medium: { ka: 'C-Print ალუმინზე', en: 'C-Print on Aluminum' },
    year: 2023,
    dimensions: { width: 100, height: 70, unit: 'cm' },
    sizeClass: 'large',
    price: { amount: 89000, currency: 'USD' },
    gelPrice: { amount: 240000, currency: 'GEL' },
    rarity: 'unique',
    isForSale: true,
    isFeatured: true,
    viewCount: 215,
    slug: 'tbilisi-nightscape',
  },
  {
    _id: 'w3',
    title: { ka: 'ქრომატული ქაოსი', en: 'Chromatic Chaos' },
    artist: MOCK_ARTISTS[1]!,
    description: {
      ka: 'ორგანული ფერის აფეთქება, მანქანით სინთეზირებული.',
      en: 'An explosion of organic color synthesized by machine.',
    },
    images: [
      'https://images.unsplash.com/photo-1541701494587-cb58502866ab?auto=format&fit=crop&w=1600&q=80',
    ],
    category: 'Abstract', categoryId: 'cat_abstract',
    medium: { ka: 'ლიმიტირებული ბეჭდვა', en: 'Limited Edition Print' },
    year: 2024,
    dimensions: { width: 60, height: 80, unit: 'cm' },
    sizeClass: 'medium',
    price: { amount: 45000, currency: 'USD' },
    gelPrice: { amount: 122000, currency: 'GEL' },
    rarity: 'limited',
    edition: { current: 12, total: 25 },
    isForSale: true,
    isFeatured: false,
    viewCount: 178,
    slug: 'chromatic-chaos',
    videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-abstract-ink-in-water-background-27200-large.mp4',
  },
  {
    _id: 'w4',
    title: { ka: 'ჩუმი არქიტექტორი', en: 'Silent Architect' },
    artist: MOCK_ARTISTS[2]!,
    description: {
      ka: 'სიზმრების გეომეტრია, არქიტექტურული სიზუსტით გამოხატული.',
      en: 'The geometry of dreams manifested through architectural precision.',
    },
    images: [
      'https://images.unsplash.com/photo-1605106702734-205df224ecce?auto=format&fit=crop&w=1600&q=80',
    ],
    category: 'Architecture', categoryId: 'cat_architecture',
    medium: { ka: 'ფოტოგრაფია', en: 'Photography' },
    year: 2024,
    dimensions: { width: 30, height: 40, unit: 'cm' },
    sizeClass: 'small',
    price: { amount: 12000, currency: 'USD' },
    gelPrice: { amount: 32500, currency: 'GEL' },
    rarity: 'open',
    isForSale: true,
    isFeatured: true,
    viewCount: 95,
    slug: 'silent-architect',
  },
  {
    _id: 'w5',
    title: { ka: 'შუქის შეხება', en: 'Touch of Light' },
    artist: MOCK_ARTISTS[0]!,
    description: {
      ka: 'სინათლის სხივი ბნელ ოთახში - სიმშვიდის მომენტი.',
      en: 'A ray of light in a dark room - a moment of tranquility.',
    },
    images: [
      'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1581337204873-ef367ad8f0a1?auto=format&fit=crop&w=800&q=80',
    ],
    category: 'Fine Art', categoryId: 'cat_fineart',
    medium: { ka: 'არქივალური ჟიკლე ბეჭდვა', en: 'Archival Giclée Print' },
    year: 2024,
    dimensions: { width: 80, height: 60, unit: 'cm' },
    sizeClass: 'medium',
    price: { amount: 34000, currency: 'USD' },
    gelPrice: { amount: 92000, currency: 'GEL' },
    rarity: 'limited',
    edition: { current: 5, total: 30 },
    isForSale: true,
    isFeatured: false,
    viewCount: 156,
    slug: 'touch-of-light',
  },
  {
    _id: 'w6',
    title: { ka: 'აეთერული ნისლი', en: 'Aetheric Mist' },
    artist: MOCK_ARTISTS[2]!,
    description: {
      ka: 'სიჩუმის ვიზუალური გამოვლინება, ალგორითმული მედიტაციის შედეგი.',
      en: 'The visual manifestation of silence, result of algorithmic meditation.',
    },
    images: [
      'https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1519750783826-e2420f4d687f?auto=format&fit=crop&w=800&q=80',
    ],
    category: 'Abstract', categoryId: 'cat_abstract',
    medium: { ka: 'ლიმიტირებული ბეჭდვა', en: 'Limited Edition Print' },
    year: 2023,
    dimensions: { width: 50, height: 70, unit: 'cm' },
    sizeClass: 'medium',
    price: { amount: 45000, currency: 'USD' },
    gelPrice: { amount: 122000, currency: 'GEL' },
    rarity: 'limited',
    edition: { current: 8, total: 20 },
    isForSale: true,
    isFeatured: false,
    viewCount: 203,
    slug: 'aetheric-mist',
  },
];

// Gift Card "artworks" — denomination-based via sizeVariants
export const MOCK_GIFT_CARD_ARTWORKS: Artwork[] = [
  {
    _id: 'gc1',
    title: { ka: 'Gallery Fotoatelier სასაჩუქრე ბარათი', en: 'Gallery Fotoatelier Gift Card' },
    artist: 'Gallery Fotoatelier',
    description: {
      ka: 'იდეალური საჩუქარი ხელოვნების მოყვარულთათვის. გამოიყენეთ ნებისმიერი ნამუშევრის შესაძენად.',
      en: 'The perfect gift for art lovers. Use it to purchase any artwork from our collection.',
    },
    images: [
      'https://images.unsplash.com/photo-1513542789411-b6a5d4f31634?auto=format&fit=crop&w=1200&q=80',
    ],
    category: 'Gift Cards', categoryId: 'cat_giftcard',
    medium: { ka: 'ციფრული სასაჩუქრე ბარათი', en: 'Digital Gift Card' },
    year: 2026,
    dimensions: { width: 0, height: 0, unit: 'cm' },
    sizeClass: 'small',
    price: { amount: 2500, currency: 'USD' },
    rarity: 'open',
    sizeVariants: [
      { _id: 'gc1_25', label: { en: '$25', ka: '$25' }, dimensions: { width: 0, height: 0, unit: 'cm' }, priceCents: 2500 },
      { _id: 'gc1_50', label: { en: '$50', ka: '$50' }, dimensions: { width: 0, height: 0, unit: 'cm' }, priceCents: 5000 },
      { _id: 'gc1_100', label: { en: '$100', ka: '$100' }, dimensions: { width: 0, height: 0, unit: 'cm' }, priceCents: 10000 },
      { _id: 'gc1_250', label: { en: '$250', ka: '$250' }, dimensions: { width: 0, height: 0, unit: 'cm' }, priceCents: 25000 },
    ],
    isForSale: true,
    isFeatured: false,
    viewCount: 45,
    slug: 'gallery-gift-card',
  },
  {
    _id: 'gc2',
    title: { ka: 'პრემიუმ სასაჩუქრე ბარათი', en: 'Premium Gift Card' },
    artist: 'Gallery Fotoatelier',
    description: {
      ka: 'პრემიუმ სასაჩუქრე ბარათი ექსკლუზიური კოლექციისთვის.',
      en: 'Premium gift card for exclusive collection access.',
    },
    images: [
      'https://images.unsplash.com/photo-1607344645866-009c320b63e0?auto=format&fit=crop&w=1200&q=80',
    ],
    category: 'Gift Cards', categoryId: 'cat_giftcard',
    medium: { ka: 'ციფრული სასაჩუქრე ბარათი', en: 'Digital Gift Card' },
    year: 2026,
    dimensions: { width: 0, height: 0, unit: 'cm' },
    sizeClass: 'small',
    price: { amount: 50000, currency: 'USD' },
    rarity: 'open',
    sizeVariants: [
      { _id: 'gc2_500', label: { en: '$500', ka: '$500' }, dimensions: { width: 0, height: 0, unit: 'cm' }, priceCents: 50000 },
      { _id: 'gc2_1000', label: { en: '$1,000', ka: '$1,000' }, dimensions: { width: 0, height: 0, unit: 'cm' }, priceCents: 100000 },
    ],
    isForSale: true,
    isFeatured: false,
    viewCount: 22,
    slug: 'premium-gift-card',
  },
];

// Merge gift cards into main artworks array
MOCK_ARTWORKS.push(...MOCK_GIFT_CARD_ARTWORKS);

export const MOCK_DROPSHOP_COLLECTIONS: DropshopCollection[] = [
  {
    _id: 'ds1',
    title: { ka: 'კავკასიის ჩრდილები', en: 'Shadows of Caucasus' },
    description: {
      ka: 'ნინო მაჭავარიანის ექსკლუზიური კოლექცია — 12 ლიმიტირებული ბეჭდვა, რომელიც კავკასიის მთების მისტიკურ სილამაზეს წარმოაჩენს.',
      en: 'An exclusive collection by Nino Machavariani — 12 limited edition prints revealing the mystical beauty of the Caucasus mountains.',
    },
    artist: MOCK_ARTISTS[0]!,
    coverImage: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1600&q=80',
    images: [
      'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1582555172866-f73bb12a2ab3?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?auto=format&fit=crop&w=1200&q=80',
    ],
    videoUrl: 'https://assets.mixkit.co/videos/preview/mixkit-abstract-flowing-purple-and-blue-neon-liquid-27201-large.mp4',
    artworkIds: ['w1', 'w5'],
    releaseDate: '2026-03-01',
    isActive: true,
    slug: 'shadows-of-caucasus',
  },
  {
    _id: 'ds2',
    title: { ka: 'ურბანული პულსი', en: 'Urban Pulse' },
    description: {
      ka: 'გიორგი ბერიძის ახალი პროექტი — თბილისის ღამის ქალაქური ფოტოგრაფიის ექსკლუზიური სერია.',
      en: "Giorgi Beridze's new project — an exclusive series of Tbilisi's nocturnal urban photography.",
    },
    artist: MOCK_ARTISTS[1]!,
    coverImage: 'https://images.unsplash.com/photo-1633167606207-d840b5070fc2?auto=format&fit=crop&w=1600&q=80',
    images: [
      'https://images.unsplash.com/photo-1633167606207-d840b5070fc2?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1541701494587-cb58502866ab?auto=format&fit=crop&w=1200&q=80',
    ],
    artworkIds: ['w2', 'w3'],
    releaseDate: '2026-04-15',
    isActive: true,
    slug: 'urban-pulse',
  },
  {
    _id: 'ds3',
    title: { ka: 'სულის პორტრეტი', en: 'Portrait of Soul' },
    description: {
      ka: 'თამარ ჯავახიშვილის ინტიმური პორტრეტების კოლექცია — ადამიანის სულის ვიზუალური ნარატივი.',
      en: "Tamar Javakhishvili's intimate portrait collection — a visual narrative of the human soul.",
    },
    artist: MOCK_ARTISTS[2]!,
    coverImage: 'https://images.unsplash.com/photo-1605106702734-205df224ecce?auto=format&fit=crop&w=1600&q=80',
    images: [
      'https://images.unsplash.com/photo-1605106702734-205df224ecce?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?auto=format&fit=crop&w=1200&q=80',
    ],
    artworkIds: ['w4', 'w6'],
    releaseDate: '2026-02-10',
    isActive: true,
    slug: 'portrait-of-soul',
  },
];

export const MOCK_EXHIBITIONS: Exhibition[] = [
  {
    _id: 'e1',
    title: { ka: 'სინათლის მეტამორფოზა', en: 'Metamorphosis of Light' },
    excerpt: { ka: '', en: '' },
    description: {
      ka: 'თანამედროვე ფოტოგრაფიის გამოფენა, სადაც სინათლე მთავარი პერსონაჟია.',
      en: 'Contemporary photography exhibition where light is the main character.',
    },
    aboutArtist: { ka: '', en: '' },
    poster: 'https://images.unsplash.com/photo-1578301978693-85fa9c0320b9?auto=format&fit=crop&w=1200&q=80',
    images: [
      'https://images.unsplash.com/photo-1578301978693-85fa9c0320b9?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1513364776144-60967b0f800f?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1545987796-200d7e3b8671?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1574182245530-967d9b3831af?auto=format&fit=crop&w=1200&q=80',
    ],
    startDate: '2026-02-01',
    endDate: '2026-03-15',
    location: { ka: 'Gallery Fotoatelier, თბილისი', en: 'Gallery Fotoatelier, Tbilisi' },
    artists: [MOCK_ARTISTS[0]!, MOCK_ARTISTS[1]!],
    isCurrent: true,
    slug: 'metamorphosis-of-light',
  },
  {
    _id: 'e2',
    title: { ka: 'კავკასიის სულები', en: 'Souls of the Caucasus' },
    excerpt: { ka: '', en: '' },
    description: {
      ka: 'პორტრეტების გამოფენა კავკასიის ხალხების შესახებ.',
      en: 'Portrait exhibition about the people of the Caucasus.',
    },
    aboutArtist: { ka: '', en: '' },
    poster: 'https://images.unsplash.com/photo-1544967082-d9d25d867d66?auto=format&fit=crop&w=1200&q=80',
    images: [
      'https://images.unsplash.com/photo-1544967082-d9d25d867d66?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1531058020387-3be344556be6?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?auto=format&fit=crop&w=1200&q=80',
    ],
    startDate: '2026-04-01',
    endDate: '2026-05-15',
    location: { ka: 'Gallery Fotoatelier, თბილისი', en: 'Gallery Fotoatelier, Tbilisi' },
    artists: [MOCK_ARTISTS[2]!],
    isCurrent: false,
    slug: 'souls-of-caucasus',
  },
];

export const MOCK_ART_FAIRS: ArtFair[] = [
  {
    _id: 'af1',
    title: { ka: 'Tbilisi Art Fair 2026', en: 'Tbilisi Art Fair 2026' },
    excerpt: { ka: '', en: '' },
    description: {
      ka: 'წლიური არტ ბაზრობა თბილისში, სადაც წარმოდგენილია საუკეთესო თანამედროვე ხელოვნება.',
      en: 'Annual art fair in Tbilisi featuring the best in contemporary art.',
    },
    poster: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?auto=format&fit=crop&w=1200&q=80',
    images: [
      'https://images.unsplash.com/photo-1513364776144-60967b0f800f?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1577720643272-265f09367456?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1572947650440-e8a97ef053b2?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1459908676235-d5f02a50184b?auto=format&fit=crop&w=1200&q=80',
    ],
    startDate: '2026-05-20',
    endDate: '2026-05-25',
    location: { ka: 'ExpoGeorgia, თბილისი', en: 'ExpoGeorgia, Tbilisi' },
    artworks: [MOCK_ARTWORKS[0]!, MOCK_ARTWORKS[1]!],
    slug: 'tbilisi-art-fair-2026',
  },
];

export const MOCK_TEAM: TeamMember[] = [
  {
    _id: 't1',
    name: { ka: 'ლუკა წერეთელი', en: 'Luka Tsereteli' },
    role: { ka: 'დამფუძნებელი და დირექტორი', en: 'Founder & Director' },
    bio: {
      ka: '20 წელზე მეტი გამოცდილება ხელოვნების სფეროში.',
      en: 'Over 20 years of experience in the art industry.',
    },
    photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=400&q=80',
    order: 1,
  },
  {
    _id: 't2',
    name: { ka: 'ანა კვარაცხელია', en: 'Ana Kvaratskhelia' },
    role: { ka: 'კურატორი', en: 'Curator' },
    bio: {
      ka: 'თანამედროვე ხელოვნების კურატორი მრავალწლიანი საერთაშორისო გამოცდილებით.',
      en: 'Contemporary art curator with extensive international experience.',
    },
    photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=400&q=80',
    order: 2,
  },
  {
    _id: 't3',
    name: { ka: 'დავით მგელაძე', en: 'Davit Mgeladze' },
    role: { ka: 'გალერეის მენეჯერი', en: 'Gallery Manager' },
    bio: {
      ka: 'ოპერაციების და კლიენტთა მომსახურების სპეციალისტი.',
      en: 'Operations and client relations specialist.',
    },
    photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=400&q=80',
    order: 3,
  },
];

export const MOCK_NEWS: NewsArticle[] = [
  {
    _id: 'news_1',
    title: {
      en: 'Gallery Fotoatelier Opens New Exhibition Space',
      ka: 'გალერეა ფოტოატელიე ახალ საგამოფენო სივრცეს ხსნის',
    },
    excerpt: {
      en: 'We are thrilled to announce the opening of our expanded exhibition hall, doubling our capacity to showcase contemporary photography.',
      ka: 'სიხარულით გაცნობებთ ჩვენი გაფართოებული საგამოფენო დარბაზის გახსნას, რაც ორმაგად გაზრდის თანამედროვე ფოტოგრაფიის ჩვენების შესაძლებლობას.',
    },
    content: {
      en: 'Gallery Fotoatelier is proud to unveil its newly expanded exhibition space on Rustaveli Avenue. The renovation, which took six months to complete, adds 200 square meters of gallery space designed specifically for large-format photography installations.\n\nThe new wing features state-of-the-art lighting systems, climate control, and modular wall configurations that allow for versatile exhibition designs. The inaugural exhibition in the new space will feature works by Georgian and international photographers exploring themes of urban transformation.\n\n"This expansion represents our commitment to providing artists with the best possible environment to present their work," said the gallery director. "We believe that great art deserves great spaces."\n\nThe public opening celebration is scheduled for the first weekend of the month, with guided tours, artist talks, and a reception.',
      ka: 'გალერეა ფოტოატელიე ამაყობს რუსთაველის გამზირზე ახლად გაფართოებული საგამოფენო სივრცის გახსნით. რენოვაცია, რომელსაც ექვსი თვე დასჭირდა, 200 კვადრატული მეტრის გალერეის სივრცეს ამატებს.\n\nახალი ფრთა აღჭურვილია თანამედროვე განათების სისტემებით, კლიმატ-კონტროლით და მოდულარული კედლის კონფიგურაციებით. სივრცეში პირველი გამოფენა წარმოადგენს ქართველი და საერთაშორისო ფოტოგრაფების ნამუშევრებს.\n\nსაჯარო გახსნის ზეიმი დაგეგმილია თვის პირველ შაბათ-კვირას.',
    },
    coverImage: 'https://images.unsplash.com/photo-1577720643272-265f09367456?auto=format&fit=crop&w=1200&q=80',
    publishedAt: '2025-12-15',
    isPublished: true,
    slug: 'gallery-opens-new-exhibition-space',
  },
  {
    _id: 'news_2',
    title: {
      en: 'Artist Spotlight: Nino Machavariani on Capturing the Caucasus',
      ka: 'ხელოვანის პორტრეტი: ნინო მაჭავარიანი კავკასიის აღბეჭდვის შესახებ',
    },
    excerpt: {
      en: 'In an exclusive interview, photographer Nino Machavariani shares her creative process and vision for her upcoming solo exhibition.',
      ka: 'ექსკლუზიურ ინტერვიუში ფოტოგრაფი ნინო მაჭავარიანი შემოქმედებით პროცესს და მომავალი პერსონალური გამოფენის ხედვას გვიზიარებს.',
    },
    content: {
      en: 'Nino Machavariani has spent the past decade documenting the breathtaking landscapes and intimate moments of daily life across the Caucasus region. Her work, characterized by a masterful use of natural light and a deep sensitivity to her subjects, has earned her recognition both locally and internationally.\n\nIn our conversation at the gallery, Nino reflected on her artistic journey: "Photography for me is about patience and presence. The Caucasus teaches you both — the mountains demand patience, and the people reward presence."\n\nHer upcoming solo exhibition at Gallery Fotoatelier will feature 40 previously unseen works from her travels through Tusheti, Svaneti, and the lesser-known valleys of eastern Georgia.\n\n"I want viewers to feel the wind, smell the pine, and hear the silence that exists in these places," she explained. "Each photograph is an invitation to slow down."',
      ka: 'ნინო მაჭავარიანი ბოლო ათწლეულის განმავლობაში კავკასიის რეგიონის მომხიბვლელ პეიზაჟებსა და ყოველდღიური ცხოვრების ინტიმურ მომენტებს აღბეჭდავს.\n\nგალერეაში ჩვენი საუბრისას ნინო შემოქმედებით გზას იხსენებს: "ფოტოგრაფია ჩემთვის მოთმინებასა და თანაგანცდას ნიშნავს. კავკასია ორივეს გასწავლის."\n\nმისი მომავალი პერსონალური გამოფენა გალერეა ფოტოატელიეში 40 ადრე უნახავ ნამუშევარს წარმოადგენს თუშეთიდან, სვანეთიდან და აღმოსავლეთ საქართველოს ნაკლებად ცნობილი ხეობებიდან.',
    },
    coverImage: 'https://images.unsplash.com/photo-1452587925148-ce544e77e70d?auto=format&fit=crop&w=1200&q=80',
    publishedAt: '2025-11-28',
    isPublished: true,
    slug: 'artist-spotlight-nino-machavariani',
  },
  {
    _id: 'news_3',
    title: {
      en: 'Gallery Fotoatelier at Tbilisi Art Fair 2025',
      ka: 'გალერეა ფოტოატელიე თბილისის არტ ბაზრობა 2025-ზე',
    },
    excerpt: {
      en: 'A recap of our participation at this year\'s Tbilisi Art Fair, where we presented works by five gallery artists.',
      ka: 'წლევანდელ თბილისის არტ ბაზრობაზე ჩვენი მონაწილეობის მიმოხილვა, სადაც გალერეის ხუთი ხელოვანის ნამუშევრები წარვადგინეთ.',
    },
    content: {
      en: 'Gallery Fotoatelier made a strong showing at the 2025 Tbilisi Art Fair, presenting a curated selection of works by five of our represented artists. The fair, held at the ExpoGeorgia exhibition center, drew collectors and art enthusiasts from across Europe and the Middle East.\n\nHighlights included Giorgi Beridze\'s abstract series "Fragments of Memory," which attracted significant collector interest, and Tamar Javakhishvili\'s portrait studies that were featured in the fair\'s official programming.\n\nThe gallery booth was designed as an immersive experience, with carefully controlled lighting that echoed our main exhibition space. Several works found new homes during the fair, and we established valuable connections with international galleries.\n\nWe look forward to an even bigger presence at next year\'s fair and thank all visitors who stopped by our booth.',
      ka: 'გალერეა ფოტოატელიემ ძლიერი პრეზენტაცია გააკეთა 2025 წლის თბილისის არტ ბაზრობაზე, ჩვენი ხუთი ხელოვანის კურირებული ნამუშევრების წარდგენით.\n\nგამორჩეული იყო გიორგი ბერიძის აბსტრაქტული სერია "მეხსიერების ფრაგმენტები" და თამარ ჯავახიშვილის პორტრეტული კვლევები.\n\nგალერეის სტენდი იმერსიულ გამოცდილებად იყო დაპროექტებული. ბაზრობის განმავლობაში რამდენიმე ნამუშევარმა ახალი სახლი იპოვა და საერთაშორისო გალერეებთან ღირებული კავშირები დავამყარეთ.',
    },
    coverImage: 'https://images.unsplash.com/photo-1531058020387-3be344556be6?auto=format&fit=crop&w=1200&q=80',
    publishedAt: '2025-10-10',
    isPublished: true,
    slug: 'gallery-at-tbilisi-art-fair-2025',
  },
];

/**
 * Get mock categories from artworks
 */
export function getMockCategories(): string[] {
  const cats = new Set(MOCK_ARTWORKS.map((a) => a.category));
  return ['All', ...Array.from(cats)];
}
